import { IX_TECH_SET, RO_TECH_SET, TECHNOLOGIES } from "@constants/global.constants";

import { TREATMENT_IDS } from "@components/WPTechnologyIcons";

import { FEED_PH_RANGES } from "@features/FeedSetup/components/FeedStreamContent/FeedpH/FeedpH.constants";

import {
  ADJUSTMENT_TYPES,
  DESIGN_FLOW_TYPES,
  DISCHARGE_PRESSURE_LEVELS,
  HANDLE_IDS,
  INITIAL_FEED_NODE_ID,
  INITIAL_PRODUCT_NODE_ID,
  MIX_INPUT_HANDLE_SET,
  NODE_TYPES,
} from "./SystemDesign.constants";
import {
  getNodeById,
  getNodeIncomingEdges,
  getNodeOutgoingEdges,
  getTreatmentNodes,
  isHighPressureRoDischarge,
} from "./SystemDesign.utils";

// ─── Drop Rules ──────────────────────────────────────────────────────────────

/**
 * Validates whether a technology can be dropped onto the canvas given the
 * treatments that already exist on it.
 *
 * Rules enforced:
 *  D1 — IXCP is a standalone-only technology and cannot coexist with other treatments.
 *  D2 — No other treatment can be added while IXCP is present on the canvas.
 *  D3 — WQA requires at least one treatment node to already be present on the canvas.
 *
 * @param {string} draggedId - The TECHNOLOGIES / TREATMENT_IDS value of the dragged item.
 * @param {Array}  nodes     - Current React Flow node array from Redux.
 * @returns {{ valid: boolean, headerKey?: string, messageKey?: string }}
 */
export const validateDropConnectivity = (draggedId, nodes) => {
  const treatmentNodes = getTreatmentNodes(nodes);
  const isIXCPOnCanvas = treatmentNodes.some(n => n.data.treatment === TECHNOLOGIES.IXCP);

  // D1 — IXCP cannot coexist with other treatments
  if (draggedId === TECHNOLOGIES.IXCP && treatmentNodes.length > 0) {
    return { valid: false, headerKey: "ixcpValidation", messageKey: "ixcpNotStandalone" };
  }

  // D2 — Cannot add other treatments when IXCP is already on canvas
  if (draggedId !== TECHNOLOGIES.IXCP && isIXCPOnCanvas) {
    return { valid: false, headerKey: "ixcpValidation", messageKey: "ixcpAlreadyPresent" };
  }

  // D3 — WQA requires at least one treatment node already on canvas
  if (draggedId === TREATMENT_IDS.WQA && treatmentNodes.length === 0) {
    return { valid: false, headerKey: "wqaValidation", messageKey: "wqaRequiresTreatment" };
  }

  return { valid: true };
};

// Message definitions kept out of the function so they're created once,
// are easy to unit-test, and read like a spec.
const MLD_BLOCK_MESSAGES = {
  notEnabled: {
    headerKey: "invalidConnection",
    messageKey: "systemDesign.mldNotEnabledMessage",
  },
  licenseExpired: {
    headerKey: "systemDesign.mldLicenseExpiredOverlayTitle",
    messageKey: "systemDesign.mldLicenseExpiredConnectionMessage",
  },
  licenseRequired: {
    headerKey: "systemDesign.mldLicenseRequired",
    messageKey: "systemDesign.mldLicenseRequiredMessage",
  },
  mldRequired: {
    headerKey: "systemDesign.mldRequired",
    messageKey: "systemDesign.noRecirculationWithoutMld",
  },
};

export const getMldRecirculationBlockMessage = (mldAccessDetails = {}) => {
  const { hasMLDAccess = false, isMldLicenseExpired = false, isMldDesignEnabled = false } = mldAccessDetails ?? {};

  if (isMldDesignEnabled) return null; // valid design, no error
  if (isMldLicenseExpired) return MLD_BLOCK_MESSAGES.licenseExpired;
  if (!hasMLDAccess) return MLD_BLOCK_MESSAGES.licenseRequired;

  return MLD_BLOCK_MESSAGES.mldRequired; // has access, not enabled
};

// ─── Connection / Edge Rules ─────────────────────────────────────────────────

/**
 * Validates whether a proposed edge connection is permitted by treatment
 * ordering and connectivity business rules.
 * Called from isValidConnection after all structural checks have already passed.
 *
 * Rules enforced:
 *  C1 — UF must connect directly to Feed or UF-waste discharge. No other node allowed.
 *  C2 — UF waste restriction: A UF discharge output may only feed another UF treatment.
 *       RO and IX cannot handle UF waste (TSS too high).
 *  C3 — IXMB must be last: IXMB.MAIN_OUT can only connect to Product or Final pH Adjust.
 *  C4 — Final pH Adjust must be last: Adjustment(PH).MAIN_OUT can only go to Product.
 *       This implicitly blocks any treatment (including IXMB) after Final pH Adjust.
 *  C5 — If WQA receives UF discharge, its MAIN_OUT may only connect to Product or UF.
 *  C6 — WQA can only receive incoming edges from Treatment or Discharge nodes.
 *  C7 — Waste recirculation requires MLD design entitlement/enablement.
 *
 * @param {object}  connection        - React Flow connection: { source, target, sourceHandle, targetHandle }
 * @param {Array}   nodes             - Current React Flow node array.
 * @param {boolean} isMldDesignEnabled - Whether MLD design mode is active for the current case.
 * @returns {{ valid: boolean, headerKey?: string, messageKey?: string }}
 */
export const validateEdgeConnectivity = (connection, nodes, mldAccessDetails, edges = []) => {
  const { source, target, sourceHandle, targetHandle } = connection;

  const sourceNode = getNodeById(source, nodes);
  const targetNode = getNodeById(target, nodes);

  if (!sourceNode || !targetNode) return { valid: true };

  // C1 — UF must connect directly to Feed or UF-waste discharge.
  //      WQA may feed UF only when that WQA is itself fed by UF discharge.
  if (
    targetNode.type === NODE_TYPES.TREATMENT &&
    targetNode.data.treatment === TECHNOLOGIES.UF &&
    targetHandle === HANDLE_IDS.MAIN_IN
  ) {
    const wqaIncomingEdge =
      sourceNode.type === NODE_TYPES.ADJUSTMENT && sourceNode.data?.adjustmentType === ADJUSTMENT_TYPES.WQA
        ? getNodeIncomingEdges(sourceNode.id, edges)?.[0]
        : null;
    const wqaIncomingSourceNode = wqaIncomingEdge ? getNodeById(wqaIncomingEdge.source, nodes) : null;
    const isWqaWithUfDischargeUpstream =
      sourceNode.type === NODE_TYPES.ADJUSTMENT &&
      sourceNode.data?.adjustmentType === ADJUSTMENT_TYPES.WQA &&
      wqaIncomingSourceNode?.type === NODE_TYPES.DISCHARGE &&
      wqaIncomingSourceNode.data?.dischargeFrom === TECHNOLOGIES.UF;

    const isValidSource =
      sourceNode.type === NODE_TYPES.FEED ||
      (sourceNode.type === NODE_TYPES.DISCHARGE && sourceNode.data?.dischargeFrom === TECHNOLOGIES.UF) ||
      isWqaWithUfDischargeUpstream;

    if (!isValidSource) {
      return { valid: false, headerKey: "invalidConnection", messageKey: "ufMustConnectFeed" };
    }
  }

  // C2 — UF waste (DISCHARGE_OUT of a UF discharge node) can only feed another UF
  //      or a WQA adjustment node.
  if (
    sourceNode.type === NODE_TYPES.DISCHARGE &&
    sourceNode.data.dischargeFrom === TECHNOLOGIES.UF &&
    sourceHandle === HANDLE_IDS.DISCHARGE_OUT
  ) {
    const isUFTarget = targetNode.type === NODE_TYPES.TREATMENT && targetNode.data.treatment === TECHNOLOGIES.UF;
    const isWQATarget =
      targetNode.type === NODE_TYPES.ADJUSTMENT && targetNode.data.adjustmentType === ADJUSTMENT_TYPES.WQA;

    if (!isUFTarget && !isWQATarget) {
      return { valid: false, headerKey: "invalidConnection", messageKey: "ufWasteOnlyUF" };
    }
  }

  // C3 — IXMB must be last: its MAIN_OUT can only go to Product or Final pH Adjust.
  //      This also implicitly blocks IXMB → RO and IXMB → UF connections.
  if (
    sourceNode.type === NODE_TYPES.TREATMENT &&
    sourceNode.data.treatment === TECHNOLOGIES.IXMB &&
    sourceHandle === HANDLE_IDS.MAIN_OUT
  ) {
    const isValidTarget =
      targetNode.type === NODE_TYPES.PRODUCT ||
      (targetNode.type === NODE_TYPES.ADJUSTMENT && targetNode.data.adjustmentType === ADJUSTMENT_TYPES.PH);

    if (!isValidTarget) {
      return { valid: false, headerKey: "invalidConnection", messageKey: "ixmbMustBeLast" };
    }
  }

  // C4 — Final pH Adjust must connect directly to Product.
  //      This also implicitly blocks any treatment (including IXMB) from appearing after it.
  if (
    sourceNode.type === NODE_TYPES.ADJUSTMENT &&
    sourceNode.data.adjustmentType === ADJUSTMENT_TYPES.PH &&
    sourceHandle === HANDLE_IDS.MAIN_OUT
  ) {
    if (targetNode.type !== NODE_TYPES.PRODUCT) {
      return { valid: false, headerKey: "invalidConnection", messageKey: "phAdjustMustBeLast" };
    }
  }

  // C5 — If WQA already receives UF discharge, its outgoing edge may only go to Product or UF.
  if (
    sourceNode.type === NODE_TYPES.ADJUSTMENT &&
    sourceNode.data.adjustmentType === ADJUSTMENT_TYPES.WQA &&
    sourceHandle === HANDLE_IDS.MAIN_OUT
  ) {
    const wqaIncomingEdge = getNodeIncomingEdges(sourceNode.id, edges)?.[0];
    if (wqaIncomingEdge) {
      const wqaIncomingSourceNode = getNodeById(wqaIncomingEdge?.source, nodes);
      const wqaReceivesUfDischarge =
        wqaIncomingSourceNode?.type === NODE_TYPES.DISCHARGE &&
        wqaIncomingSourceNode.data?.dischargeFrom === TECHNOLOGIES.UF;
      const wqaFeedsAllowedTarget =
        targetNode.type === NODE_TYPES.PRODUCT ||
        (targetNode.type === NODE_TYPES.TREATMENT && targetNode.data?.treatment === TECHNOLOGIES.UF);

      if (wqaReceivesUfDischarge && !wqaFeedsAllowedTarget) {
        return { valid: false, headerKey: "wqaValidation", messageKey: "wqaUfDischargeRequiresProductOrUf" };
      }
    }
  }

  // C6 — WQA can only receive an incoming edge from a treatment node or any discharge node.
  //      Feed Water and all other non-technology nodes (other adjustments, split, mix, product)
  //      cannot connect directly into WQA.
  if (targetNode.type === NODE_TYPES.ADJUSTMENT && targetNode.data.adjustmentType === ADJUSTMENT_TYPES.WQA) {
    const isValidWQASource = sourceNode.type === NODE_TYPES.TREATMENT || sourceNode.type === NODE_TYPES.DISCHARGE;

    const wqaOutgoingEdge = getNodeOutgoingEdges(targetNode.id, edges)?.[0];

    const isUfDischargeSource =
      sourceNode.type === NODE_TYPES.DISCHARGE && sourceNode.data?.dischargeFrom === TECHNOLOGIES.UF;

    if (wqaOutgoingEdge) {
      const { type: wqaOutgoingTargetNodeType, data: wqaOutgoingTargetNodeData } = getNodeById(
        wqaOutgoingEdge?.target,
        nodes,
      );

      const wqaConnectedToUf =
        wqaOutgoingTargetNodeType === NODE_TYPES.TREATMENT && wqaOutgoingTargetNodeData?.treatment === TECHNOLOGIES.UF;
      const wqaConnectedToProductOrUf = wqaOutgoingTargetNodeType === NODE_TYPES.PRODUCT || wqaConnectedToUf;

      if (isUfDischargeSource && !wqaConnectedToProductOrUf) {
        return { valid: false, headerKey: "wqaValidation", messageKey: "wqaUfDischargeRequiresProductOrUf" };
      }

      if (wqaConnectedToUf && !isUfDischargeSource) {
        return { valid: false, headerKey: "wqaValidation", messageKey: "wqaEdgeMustFromUF" };
      }
    }

    if (!isValidWQASource) {
      return { valid: false, headerKey: "wqaValidation", messageKey: "wqaEdgeMustFromTreatment" };
    }
  }

  // C7 — Waste recirculation requires MLD design to be enabled.
  // If user does not have MLD entitlement (or license expired),
  // show MLD-access error. Otherwise show standard invalid connection error.
  if (sourceNode.type === NODE_TYPES.DISCHARGE && sourceHandle === HANDLE_IDS.DISCHARGE_OUT) {
    const processFlowTargetTypes = new Set([
      NODE_TYPES.TREATMENT,
      NODE_TYPES.MIX,
      NODE_TYPES.SPLIT,
      NODE_TYPES.ADJUSTMENT,
    ]);
    const isProcessFlowTarget = processFlowTargetTypes.has(targetNode.type);

    if (isProcessFlowTarget) {
      const mldError = getMldRecirculationBlockMessage(mldAccessDetails);

      if (mldError) return { valid: false, ...mldError };
    }
  }

  return { valid: true };
};

// ─── Design Completeness Validation ──────────────────────────────────────────

/**
 * Required handles per node type.
 * For Split: MAIN_IN + at least 2 of the 3 source handles (right/top/bottom).
 * For Mix: MAIN_OUT + at least 2 of the 3 target handles (left/top/bottom).
 * Discharge: only DISCHARGE_IN is required; DISCHARGE_OUT is optional.
 */
const REQUIRED_HANDLES = {
  [NODE_TYPES.FEED]: { sources: [HANDLE_IDS.MAIN_OUT], targets: [] },
  [NODE_TYPES.TREATMENT]: { sources: [HANDLE_IDS.MAIN_OUT], targets: [HANDLE_IDS.MAIN_IN] },
  [NODE_TYPES.ADJUSTMENT]: { sources: [HANDLE_IDS.MAIN_OUT], targets: [HANDLE_IDS.MAIN_IN] },
  [NODE_TYPES.PRODUCT]: { sources: [], targets: [HANDLE_IDS.MAIN_IN] },
  [NODE_TYPES.DISCHARGE]: { sources: [], targets: [HANDLE_IDS.DISCHARGE_IN] },
  [NODE_TYPES.SPLIT]: {
    sources: [],
    targets: [HANDLE_IDS.MAIN_IN],
    minSources: { handles: [HANDLE_IDS.SPLIT_RIGHT, HANDLE_IDS.SPLIT_TOP, HANDLE_IDS.SPLIT_BOTTOM], count: 2 },
  },
  [NODE_TYPES.MIX]: {
    sources: [HANDLE_IDS.MAIN_OUT],
    targets: [],
    minTargets: { handles: [HANDLE_IDS.MIX_LEFT, HANDLE_IDS.MIX_TOP, HANDLE_IDS.MIX_BOTTOM], count: 2 },
  },
};

/**
 * Returns a list of nodes with missing required connections.
 * Each entry includes the node name and whether the missing connection
 * is "incoming" (target handle) or "outgoing" (source handle).
 *
 * @param {Array} nodes - Current React Flow node array.
 * @param {Array} edges - Current React Flow edge array.
 * @returns {Array<{ name: string, direction: 'incoming' | 'outgoing' }>}
 */
export const getMissingConnections = (nodes, edges) => {
  const missing = [];
  if (!nodes?.length) return missing;

  for (const node of nodes) {
    const req = REQUIRED_HANDLES[node.type];
    if (!req) continue;

    const nodeName = node.data?.name || node.id;

    // Check required source handles (outgoing)
    const missingSource = req.sources.some(
      handleId => !edges.some(e => e.source === node.id && e.sourceHandle === handleId),
    );
    // Check minimum source handle count (Split)
    const missingMinSources =
      req.minSources &&
      req.minSources.handles.filter(handleId => edges.some(e => e.source === node.id && e.sourceHandle === handleId))
        .length < req.minSources.count;

    if (missingSource || missingMinSources) {
      missing.push({ name: nodeName, direction: "outgoing" });
    }

    // Check required target handles (incoming)
    const missingTarget = req.targets.some(
      handleId => !edges.some(e => e.target === node.id && e.targetHandle === handleId),
    );
    // Check minimum target handle count (Mix)
    const missingMinTargets =
      req.minTargets &&
      req.minTargets.handles.filter(handleId => edges.some(e => e.target === node.id && e.targetHandle === handleId))
        .length < req.minTargets.count;

    if (missingTarget || missingMinTargets) {
      missing.push({ name: nodeName, direction: "incoming" });
    }
  }

  return missing;
};

/**
 * Checks whether all required handles on every node have at least one edge.
 * Returns true if the design is complete (all required connections present).
 *
 * @param {Array} nodes - Current React Flow node array.
 * @param {Array} edges - Current React Flow edge array.
 * @returns {boolean}
 */
export const isDesignComplete = (nodes, edges) => {
  if (!nodes?.length) return false;

  // At least one treatment node is required for a valid design
  if (!nodes.some(n => n.type === NODE_TYPES.TREATMENT)) return false;

  return getMissingConnections(nodes, edges).length === 0;
};

/**
 * Checks whether the primary product node (`product-1`) is connected
 * (directly or indirectly) to a UF/IX waste discharge path.
 *
 * Rule intent:
 *  - Applies only to the primary product node.
 *  - Secondary product nodes are intentionally ignored.
 *  - Runs only when Product flow type is selected.
 *  - If Feed flow type is selected, returns false.
 *  - Backtrack upstream from the primary Product node.
 *  - If any upstream path includes a UF/IX Discharge node via DISCHARGE_OUT,
 *    the design is invalid.
 *  - For Split nodes, backtracking follows the single incoming edge to Split.
 *    This means whichever split output feeds primary Product, validation
 *    still evaluates the Split's one upstream source for UF/IX waste ancestry.
 *
 * @param {Array} nodes - Current React Flow node array.
 * @param {Array} edges - Current React Flow edge array.
 * @param {string} flowType - Current flow type (Feed/Product).
 * @returns {boolean} True when primary product is on a UF/IX waste path.
 */
export const isPrimaryProductConnectedToUfIxWastePath = (nodes, edges, flowType) => {
  if (!nodes?.length || !edges?.length) return false;
  if (flowType !== DESIGN_FLOW_TYPES.PRODUCT) return false;

  const primaryFeedNode = getNodeById(INITIAL_FEED_NODE_ID, nodes);
  if (!primaryFeedNode) return false;

  const primaryProductNode = getNodeById(INITIAL_PRODUCT_NODE_ID, nodes);
  if (!primaryProductNode) return false;

  const visitedNodeIds = new Set();
  const queue = [INITIAL_PRODUCT_NODE_ID];

  while (queue.length > 0) {
    const currentNodeId = queue.shift();
    if (!currentNodeId || visitedNodeIds.has(currentNodeId)) continue;
    visitedNodeIds.add(currentNodeId);

    const incomingEdges = getNodeIncomingEdges(currentNodeId, edges);

    for (const edge of incomingEdges) {
      const upstreamNode = getNodeById(edge.source, nodes);
      if (!upstreamNode) continue;

      const isUfIxWasteDischargeSource =
        upstreamNode.type === NODE_TYPES.DISCHARGE &&
        edge.sourceHandle === HANDLE_IDS.DISCHARGE_OUT &&
        (upstreamNode.data?.dischargeFrom === TECHNOLOGIES.UF || IX_TECH_SET.has(upstreamNode.data?.dischargeFrom));

      if (isUfIxWasteDischargeSource) {
        return true;
      }

      queue.push(edge.source);
    }
  }

  return false;
};

// ─── pH Node Data Validation ─────────────────────────────────────────────────

/**
 * Returns a list of pH adjustment nodes whose toolbar data is not filled or valid.
 * A node is invalid if:
 *  - No chemical (acid or alkali) has been selected, OR
 *  - The targetPh value is outside the valid range defined by FEED_PH_RANGES.
 *
 * @param {Array} nodes - Current React Flow node array.
 * @returns {Array<{ id: string, name: string, missingChemical: boolean, missingTargetPh: boolean, invalidTargetPh: boolean }>}
 */
export const getInvalidPhNodes = nodes => {
  const invalid = [];
  if (!nodes?.length) return invalid;

  for (const node of nodes) {
    if (!(node.type === NODE_TYPES.ADJUSTMENT && node.data?.adjustmentType === ADJUSTMENT_TYPES.PH)) continue;

    const { min: minPH, max: maxPH } = FEED_PH_RANGES.hard;

    const { acidChemicalId, alkaliChemicalId, acidChemical, alkaliChemical, targetPh } = node.data || {};
    const hasAcid = Boolean(acidChemicalId || acidChemical?.symbol);
    const hasAlkali = Boolean(alkaliChemicalId || alkaliChemical?.symbol);
    const missingChemical = !hasAcid || !hasAlkali;
    const missingTargetPh = targetPh == null || targetPh === 0;
    const invalidTargetPh = !missingTargetPh && (Number(targetPh) < minPH || Number(targetPh) > maxPH);

    if (missingChemical || missingTargetPh || invalidTargetPh) {
      invalid.push({
        id: node.id,
        name: node.data?.name || node.id,
        missingChemical,
        missingTargetPh,
        invalidTargetPh,
      });
    }
  }

  return invalid;
};

const mergePressureLevels = (levelA, levelB) => {
  if (!levelA) return levelB;
  if (!levelB) return levelA;
  return levelA === levelB ? levelA : DISCHARGE_PRESSURE_LEVELS.HIGH_AND_LOW;
};

const getDischargePressureLevel = node => {
  const isRODischarge = node?.data?.dischargeFrom === TECHNOLOGIES.RO;

  return isRODischarge && isHighPressureRoDischarge(node?.data)
    ? DISCHARGE_PRESSURE_LEVELS.HIGH
    : DISCHARGE_PRESSURE_LEVELS.LOW;
};

const getPressureLevelForNode = ({ nodeId, nodeMap, incomingEdgesByTarget, mixPressureCache, inProgressMixes }) => {
  const node = nodeMap.get(nodeId);

  if (node.type === NODE_TYPES.TREATMENT || node.type === NODE_TYPES.FEED) {
    return DISCHARGE_PRESSURE_LEVELS.LOW;
  }

  if (node.type === NODE_TYPES.DISCHARGE) {
    return getDischargePressureLevel(node);
  }

  if (node.type === NODE_TYPES.MIX && mixPressureCache.has(nodeId)) {
    return mixPressureCache.get(nodeId);
  }

  if (node.type === NODE_TYPES.MIX && inProgressMixes.has(nodeId)) {
    return DISCHARGE_PRESSURE_LEVELS.LOW; // Prevent infinite recursion for circular Mix connections
  }

  if (node.type === NODE_TYPES.MIX) {
    inProgressMixes.add(nodeId);
  }

  let pressureLevel = null;
  const mixIncomingEdges = incomingEdgesByTarget
    .get(nodeId)
    ?.filter(edge => node.type !== NODE_TYPES.MIX || MIX_INPUT_HANDLE_SET.has(edge.targetHandle));

  for (const edge of mixIncomingEdges) {
    const upstreamPressureLevel = getPressureLevelForNode({
      nodeId: edge.source,
      nodeMap,
      incomingEdgesByTarget,
      mixPressureCache,
      inProgressMixes,
    });
    pressureLevel = mergePressureLevels(pressureLevel, upstreamPressureLevel);
  }

  if (node.type === NODE_TYPES.MIX) {
    inProgressMixes.delete(nodeId);
    mixPressureCache.set(nodeId, pressureLevel);
  }

  return pressureLevel;
};

/**
 * Returns all Mix nodes that merge high-pressure and low-pressure streams.
 *
 * Pressure level rules:
 *  - Treatment output is low pressure.
 *  - Discharge output is low pressure, except RO discharge configured as:
 *      - Concentrate pressure, or
 *      - Specify pressure > 0.
 *  - Mix inputs are resolved recursively by backtracking upstream through
 *    intermediate nodes until treatment/discharge sources are reached.
 *
 * @param {Array} nodes - Current React Flow node array.
 * @param {Array} edges - Current React Flow edge array.
 * @returns {Array<{ id: string, name: string }>} Invalid mix nodes.
 */
export const getInvalidMixPressureNodes = (nodes, edges) => {
  if (!nodes?.length || !edges?.length) return [];

  const hasHighPressureRoDischarge = nodes.some(
    node =>
      node.type === NODE_TYPES.DISCHARGE &&
      node.data?.dischargeFrom === TECHNOLOGIES.RO &&
      isHighPressureRoDischarge(node?.data),
  );

  if (!hasHighPressureRoDischarge) return [];

  const nodeMap = new Map(nodes.map(node => [node.id, node]));
  const incomingEdgesByTarget = new Map();

  for (const edge of edges) {
    if (!incomingEdgesByTarget.has(edge.target)) {
      incomingEdgesByTarget.set(edge.target, []);
    }
    incomingEdgesByTarget.get(edge.target).push(edge);
  }

  const mixPressureCache = new Map();
  const invalidMixNodes = [];

  for (const mixNode of nodes) {
    if (mixNode.type !== NODE_TYPES.MIX) continue;

    const inputEdges = incomingEdgesByTarget
      .get(mixNode.id)
      ?.filter(edge => MIX_INPUT_HANDLE_SET.has(edge.targetHandle));

    if (inputEdges.length !== 2) continue;

    const [firstInputPressureLevel, secondInputPressureLevel] = inputEdges.map(edge =>
      getPressureLevelForNode({
        nodeId: edge.source,
        nodeMap,
        incomingEdgesByTarget,
        mixPressureCache,
        inProgressMixes: new Set([mixNode.id]),
      }),
    );

    const mixedOutputPressureLevel = mergePressureLevels(firstInputPressureLevel, secondInputPressureLevel);
    const mixesHighAndLow = mixedOutputPressureLevel === DISCHARGE_PRESSURE_LEVELS.HIGH_AND_LOW;

    if (mixesHighAndLow) {
      invalidMixNodes.push({
        id: mixNode.id,
        name: mixNode?.data?.name || mixNode.id,
      });
    }
  }

  return invalidMixNodes;
};

// ─── RO-IX Waste Warning ─────────────────────────────────────────────────────

/**
 * Returns a list of RO treatment node IDs that receive IX waste water, either
 * directly or through intermediate Mix / Split nodes.
 *
 * Traversal rules:
 *  - Seed: all edges leaving an IX discharge node via DISCHARGE_OUT.
 *  - RO/CCRO treatment nodes reached → added to the warning list.
 *  - Mix or Split nodes reached → traversal continues through their outgoing edges
 *    so that IX waste propagated via mixing or splitting is also detected.
 *  - Any other node type stops the traversal on that branch.
 *
 * @param {Array} nodes - Current React Flow node array.
 * @param {Array} edges - Current React Flow edge array.
 * @returns {string[]} Array of RO node IDs that are receiving IX waste.
 */
export const getRoNodesConnectedToIxWaste = (nodes, edges) => {
  if (!nodes?.length || !edges?.length) return [];

  const ixDischargeNodeIds = new Set(
    nodes.filter(n => n.type === NODE_TYPES.DISCHARGE && IX_TECH_SET.has(n.data?.dischargeFrom)).map(n => n.id),
  );

  if (ixDischargeNodeIds.size === 0) return [];

  // Build a forward-adjacency map for efficient traversal
  const outgoingEdges = new Map();
  for (const edge of edges) {
    if (!outgoingEdges.has(edge.source)) outgoingEdges.set(edge.source, []);
    outgoingEdges.get(edge.source).push(edge);
  }

  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const roNodeIds = new Set();
  const visited = new Set();

  // Seed BFS from IX discharge DISCHARGE_OUT edges
  const queue = [];
  for (const ixDischargeId of ixDischargeNodeIds) {
    for (const edge of outgoingEdges.get(ixDischargeId) || []) {
      if (edge.sourceHandle === HANDLE_IDS.DISCHARGE_OUT) {
        queue.push(edge.target);
      }
    }
  }

  while (queue.length > 0) {
    const nodeId = queue.shift();
    if (!nodeId || visited.has(nodeId)) continue;
    visited.add(nodeId);

    const node = nodeMap.get(nodeId);
    if (!node) continue;

    if (node.type === NODE_TYPES.TREATMENT && RO_TECH_SET.has(node.data?.treatment)) {
      roNodeIds.add(node.id);
    } else if (node.type === NODE_TYPES.MIX || node.type === NODE_TYPES.SPLIT) {
      // IX waste influence propagates through Mix and Split nodes
      for (const edge of outgoingEdges.get(nodeId) || []) {
        queue.push(edge.target);
      }
    }
  }

  return [...roNodeIds];
};
